import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { RequirementsSolved } from '../../../../../api';
import { IconComponent, Icons } from '@ansys-internal/awc-angular/icons';
import { ColorBackground } from '@ansys-internal/awc-angular/core';
import { CommonModule } from '@angular/common';
import { ResultType } from '../results-list.component';
import { DriveCycleWarningDialogComponent } from '../../../dialogs/drive-cycle-warning-dialog/drive-cycle-warning-dialog.component';
import { CapabilityCurveErrorDialogComponent } from '../../../dialogs/capability-curve-error-dialog/capability-curve-error-dialog.component';
import { DialogService } from '@ansys-internal/awf-angular/shared';
import { ReplaySubject, startWith, takeUntil } from 'rxjs';
import {
  CompareJobsExecuteEnum,
  CompareJobsState,
  CompareJobsStateModel,
} from '../../../../state/lib/compare-jobs.state';
import { State } from '@ansys-internal/awf-angular/store';
import { AccordionComponent } from '@ansys-internal/awc-angular/accordions';
import { Job } from '@ansys/cloud-angular-client';
import { CevActionsService } from '../../../../shared/services/cev-actions.service';
import {
  PageState,
  PageStateModel,
} from '../../../../state/lib/app-page.state';
import { ConceptSubSection } from '../../../../shared/enums';
import { JobState } from '../../../../state/lib/jobs.state';
import { ResultsService } from '../../../../shared/services/results.services';
import { ProgressBarComponent } from '@ansys-internal/awc-angular/progress-bar';
import {AlertComponent} from "../../../dialogs/alert/alert.component";
import {ModalSize} from "@ansys-internal/awc-angular/modal";

@Component({
    selector: 'app-result-item',
    imports: [
        IconComponent,
        CommonModule,
        AccordionComponent,
        ProgressBarComponent,
    ],
    templateUrl: './result-item.component.html',
    styleUrl: './result-item.component.scss'
})
export class ResultItemComponent {
  @Input() results: RequirementsSolved[] = [];
  @Input() $resultsLoaded: boolean = false;
  @Input() selectedResult: string = '';
  @Output() protected jobDeselected: EventEmitter<boolean> = new EventEmitter();
  @Output() protected resultSelected: EventEmitter<RequirementsSolved> =
    new EventEmitter();
  protected readonly resultType = ResultType;
  protected readonly icons = Icons;
  protected readonly colorBackground = ColorBackground;
  protected readonly Object = Object;
  protected jobsToCompare: any[] | null = null;
  protected resultsToCompare: RequirementsSolved[] | null = null;
  protected page!: PageStateModel;
  protected readonly ConceptSubSection = ConceptSubSection;
  protected readonly Icons = Icons;
  private jobNameMap: Record<string, string> = {};
  private ngUnsubscribe: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private dialog: DialogService,
    private state: State,
    private _cdr: ChangeDetectorRef,
    private cevActions: CevActionsService,
    protected resultsService: ResultsService
  ) {}

  ngOnInit(): void {
    this.state
      .get(CompareJobsState)
      .pipe(
        startWith(this.state.value(CompareJobsState)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((data: CompareJobsStateModel) => {
        this.jobsToCompare = data.jobs;
        this.resultsToCompare = data.resultsToCompare;
        this._cdr.detectChanges();
      });

    this.state
      .get(PageState)
      .pipe(
        startWith(this.state.value(PageState)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((data: PageStateModel) => {
        this.page = data;
        this._cdr.detectChanges();
      });
  }

  deselectJob(redirect?: boolean): void {
    this.jobDeselected.emit(redirect);
  }

  private getErrorCode(result: RequirementsSolved): number | null | undefined {
    return 'error_code' in result ? (result as any).error_code : undefined;
  }

  selectResult(result: RequirementsSolved): void {
    if (this.getErrorCode(result)) {
      this.onRequirementWarning(result);
    } else {
      this.resultSelected.emit(result);
    }
  }

  async onCompareResult(result: RequirementsSolved, job: Job): Promise<void> {
    if (!result.id) return;
    if (this.isCompareSelectedOfDifferentType(result)) {
      return;
    }

    const compare = await this.cevActions.execute(
      CompareJobsExecuteEnum.GET_RESULT_TO_COMPARE,
      result.id
    );

    if (!compare.result) {
      const resultWithJobId = { ...result, jobId: job?.jobId ?? 'Unknown' };
      this.cevActions.execute(CompareJobsExecuteEnum.ADD_RESULT_TO_COMPARE, [
        resultWithJobId,
      ]);
    } else {
      this.cevActions.execute(
        CompareJobsExecuteEnum.REMOVE_RESULT_FROM_COMPARE,
        result.id
      );
    }
  }

  public onDriveCycleWarning(result: RequirementsSolved): void {
    this.dialog.open(DriveCycleWarningDialogComponent, {
      size: ModalSize.LARGE,
      title: 'Drive Cycle Warning',
      data: { result },
    });
  }

  public onCapabilityCurveError(result: RequirementsSolved): void {
    this.dialog.open(CapabilityCurveErrorDialogComponent, {
      size: ModalSize.LARGE,
      title: 'Capability Curve Errors',
      data: { result },
    });
  }

  public onRequirementWarning(result: RequirementsSolved): void {
    this.dialog.open(AlertComponent, {
      size: ModalSize.LARGE,
      title: 'Requirement Error',
      data: {
        message: 'Error code: ' + this.getErrorCode(result) + '. ' + result.outcome_message,
      },
    });
  }

  protected isCompareSelected(resultId: string): boolean {
    return (
      this.resultsToCompare?.some(
        (requirementSolved) => requirementSolved.id === resultId
      ) ?? false
    );
  }

  protected isCompareSelectedOfDifferentType(
    result: RequirementsSolved
  ): boolean {
    return (
      this.resultsToCompare?.some(
        (requirementSolved) =>
          requirementSolved.requirement_solved_type !==
          result.requirement_solved_type
      ) ?? false
    );
  }
}
